<!-- NOTE: slot text should be wrapped in a <span> to avoid default styles (e.g. <p>) -->
<script setup lang="ts">
import { onKeyStroke, onClickOutside } from '@vueuse/core';
import { onMounted, onUnmounted, ref } from 'vue';

import AppOverlay from '../AppOverlay.vue';
import { KeyboardKeyCode } from '../../../models';
import {
  blockBackgroundScroll,
  enableBackgroundScroll,
} from '../../../helpers/blockBackgroundScroll';

interface AppModalProps {
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  maxWidth?: string;
}

withDefaults(defineProps<AppModalProps>(), {
  height: '100%',
  minHeight: '478px',
  maxHeight: '720px',
  maxWidth: '1160px',
});

interface AppModalEmits {
  (eventName: 'close'): void;
}

const emit = defineEmits<AppModalEmits>();

const modalRef = ref<HTMLDivElement | null>(null);

onClickOutside(modalRef, (event) => {
  // NOTE: Without this modal is closed, when closing dropdown is expected
  const dropdown = document.querySelector('#appSelectDropdown');
  if (dropdown && dropdown.contains(event.target as Node)) {
    return;
  }

  // NOTE: Without this modal is closed, when closing tooltip is expected
  const tooltipOverlay = document.querySelector('#appTooltipOverlay');
  if (tooltipOverlay && tooltipOverlay.contains(event.target as Node)) {
    return;
  }

  emit('close');
});

onKeyStroke(KeyboardKeyCode.Escape, () => emit('close'));

onMounted(() => {
  blockBackgroundScroll();
});

onUnmounted(() => enableBackgroundScroll());
</script>

<template>
  <AppOverlay :is-modal="true" @close="emit('close')">
    <div ref="modalRef" class="app-modal">
      <slot />
    </div>
  </AppOverlay>
</template>

<style lang="scss" scoped>
@use '../../../styles/media' as *;
@use '../../../styles/fonts' as *;

.app-modal {
  --small-padding: var(--spacing-16);
  --height: v-bind(height);
  --max-width: v-bind(maxWidth);
  --max-height: v-bind(maxHeight);
  --min-height: v-bind(minHeight);

  gap: var(--spacing-24);

  width: 100%;
  max-width: var(--max-width);

  height: var(--height);
  min-height: var(--min-height);
  max-height: var(--max-height);

  box-sizing: border-box;
  z-index: var(--modal-z-index);
  background-color: var(--color-white);
  border-radius: var(--border-radius-12);
  overflow: hidden;

  @include for-less-or-equal-than-900-px {
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }
}
</style>
